import dayjs from "dayjs";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { format2, format4, numberWithCommas, separateNumberWithoutFormat } from "../utils/numbers";

import Modal from "bootstrap/js/dist/modal";
import TransactionDetail from "./modals/transaction-detail";
import { useAuth } from "../store/AuthContext";

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { notificationAPI } from "../utils/notification";
import { useNotification } from "../layouts/home_layout";

dayjs.extend(utc);
dayjs.extend(timezone);

function parseDate(item) {
  if (item?.createdAt != null) {
    return dayjs(item?.createdAt);
  }
  return dayjs(item?.createdAt);
}

function formatTimeStr(time) {
  return dayjs(time).format("h:mm A");
}

function getDateLabel(dateObj) {
  const day = dateObj.startOf("day");
  const today = dayjs().startOf("day");
  const diff = today.diff(day, "day");

  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return day.format("DD-MMM-YYYY");
}

function getTransactionType(txn, user) {
  let t = "";
  switch (txn.txnr_type) {
    case "redemptionofunits":
      t = "Redemption";
      break;
    case "saleofunit":
      t = "Investment";
      break;
    case "conversionin":
      t = "Conversion In";
      break;
    case "conversionout":
      t = "Conversion Out";
      break;
    case "transferofunits":
      if (txn.folio_no === user?.data.folio_no) {
        t = "Transfer Out";
      } else {
        t = "Transfer In";
      }
      break;
  }
  return t;
}

function getTransactionColor(txn, user, theme) {
  switch (txn.txnr_type) {
    case "redemptionofunits":
      return theme === 'dark' ? "rgb(238, 68, 59)" : "rgb(217, 44, 35)";
    case "saleofunit":
      return theme === 'dark' ? "rgb(15, 189, 23)" : "rgb(8, 167, 16)";
    case "conversionout":
      return theme === 'dark' ? "rgb(238, 68, 59)" : "rgb(217, 44, 35)";
    case "conversionin":
      return theme === 'dark' ? "rgb(15, 189, 23)" : "rgb(8, 167, 16)";
    case "transferofunits":
      if (txn.folio_no === user?.data.folio_no) {
        return theme === 'dark' ? "rgb(238, 68, 59)" : "rgb(217, 44, 35)";
      } else {
        return theme === 'dark' ? "rgb(15, 189, 23)" : "rgb(8, 167, 16)";
      }
  }
}

const successTypes = ["TXN_SUCCESS", "REQ_ACCEPTED", "REQ_PAYMENT_VERIFIED"]
const rejectTypes = ["TXN_FAILED", "REQ_REJECTED", "REQ_PAYMENT_REJECTED"]

export default function Notifications() {
  const [screenSize, setScreenSize] = useState();
  const [initialUnread, setInitialUnread] = useState(null)
//   const [activeTransaction, setActiveTransaction] = useState(null);
  const { theme } = useTheme();
  const { user } = useAuth();

  // const { notifications: data } = useOutletContext()
  const { notifications: data, updateNotifications } = useNotification()

  const { t } = useTranslation();

  const transactionModalRef = useRef(null);
  const transactionModalInstanceRef = useRef(null);


//   function handleTransactionSelect(txnData) {
//     setActiveTransaction(txnData);

//     if (transactionModalRef.current) {
//       transactionModalInstanceRef.current?.toggle();
//     }
//   }

  useEffect(() => {
    if (transactionModalRef.current) {
      transactionModalInstanceRef.current = new Modal(transactionModalRef.current, { backdrop: true, keyboard: true });
    }

    const resizeScreen = () => {
      setScreenSize(window?.innerWidth);
    };

    resizeScreen();
    window.addEventListener("resize", resizeScreen);

    return () => {
      window.removeEventListener("resize", resizeScreen);
    };
  }, []);

  useEffect(() => {
    if (!data || !user) return;
    if (!user?.data?.folio_no) return;
    if (data?.length === 0) return;

    let allRead = data.every(notification => notification.isRead);

    if (allRead) return;

    if (!initialUnread) {
      const unread = data.filter(notification => !notification.isRead);

      if (unread.length > 0) {
        setInitialUnread(unread);
      }
    }

    const notificationIds = JSON.stringify(data.map(notification => notification._id) || "[]")

    const handleNotificationsUpdate = async () => {
      try {
        await notificationAPI.markAsRead(user.data.folio_no, notificationIds)

        const { notifications, unreadCount } =  await notificationAPI.getNotifications(undefined, { folio_no: user.data.folio_no })

        updateNotifications(notifications, unreadCount);

      } catch (e) {
        // 
      }
    }

    handleNotificationsUpdate();

  }, [data, initialUnread])

  if (data.length === 0 || data === null || data === undefined) {
    return (
      <div
        className='w-100'
        style={{
          borderRadius: "20px",
        //   marginTop: "12px",
          backgroundColor: "var(--theme-bg)",
        maxHeight: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
        minHeight: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
        //   height: screenSize <= 575 ? "calc(100dvh - 212px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 265px)" : "calc(100dvh - 192px)",
        }}
      >
        <div
          className='w-100 position-absolute start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
          style={{
            color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
            top: "50%",
          }}
        >
          <div
            className=''
            role='status'
            style={{
              fontSize: "1.2rem",
              color: theme === "dark" ? "rgb(200,200,200)" : "rgba(80, 80, 80, 1)",
            }}
          >
            {t("No Notifications Found")}
          </div>
        </div>
      </div>
    );
  }

  const initialUnreadIds = useMemo(() => {
    if (!initialUnread || initialUnread.length === 0) return new Set();
    return new Set(initialUnread.map(n => n._id))
  }, [initialUnread])

  const remainingNotifications = data ? data?.filter(n => !initialUnreadIds.has(n._id)) : [];

  const sorted = [...remainingNotifications].sort((a, b) => parseDate(b).diff(parseDate(a)));

  const groups = sorted.reduce((map, item) => {
    const dateObj = parseDate(item);
    const label = getDateLabel(dateObj);
    if (!map.has(label)) map.set(label, []);
    map.get(label).push({ ...item, _dateObj: dateObj });
    return map;
  }, new Map());

  return (
      <div
        className='w-100'
        style={{
          borderRadius: "20px",
        //   marginTop: "12px",
          backgroundColor: "var(--theme-bg)",
        maxHeight: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
        minHeight: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
        //   height: screenSize <= 575 ? "calc(100dvh - 212px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 265px)" : "calc(100dvh - 192px)",
        }}
      >
        <div
          className={
            "row w-100 mx-auto flex-column position-relative flex-nowrap"
          }
          style={{
            rowGap: "1rem",
            // height: screenSize >= 992
            //       ? "calc(100dvh - 192px)"
            //       : screenSize <= 575
            //         ? "calc(100dvh - 212px)"
            //         : "calc(100dvh - 265px)",
            padding: screenSize >= 576 ? ('.8rem 1rem') : ('.25rem .75rem'),
            overflowY: "hidden",
            // scrollbarWidth: screenSize >= 768 && activeSubMenu === "statement" ? "" : "none",
            borderBottomLeftRadius: screenSize <= 575 ? "20px" : "",
            borderBottomRightRadius: screenSize <= 575 ? "20px" : "",
          }}
        >
          <div className='col-12 p-0 d-flex flex-column pt-2'>
          <div className="w-100 text-center py-1" style={{
            fontSize: '24px',
            letterSpacing: '.02em',
            color: theme === 'dark' ? 'var(--theme-light-secondary)' : 'var(--theme-light-primary)'
          }}>
            Notifications
          </div>
              {/* {loading ? (
                <div
                  className='position-relative w-100'
                  style={{
                    height: "calc(100dvh - 340px)",
                  }}
                >
                  <div
                    className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                    style={{
                      color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                    }}
                  >
                    <div
                      className=''
                      role='status'
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "300",
                        letterSpacing: ".02em",
                      }}
                    >
                      {t("app.loading.stmnt")}
                    </div>
                    <div
                      className='spinner-border'
                      style={{
                        fontSize: "11px",
                        height: "24px",
                        width: "24px",
                        color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                      }}
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                </div>
              ) : ( */}
          <div className={`mt-3 flex-column flex-nowrap p-0 pe-sm-1 pb-2 pb-sm-0${(theme === "dark" ? " add-custom-scroll-dark" : " add-custom-scroll")}`} style={{
            overflowY: 'auto',
            scrollbarWidth: screenSize >= 768 ? '' : "none",
                  height: screenSize >= 992
                        ? "calc(100dvh - 209px)"
                        : screenSize <= 575
                          ? "calc(100dvh - 220px)"
                          : "calc(100dvh - 283px)",
          }}>

              <div
                className='mb-3'
                style={{
                  fontSize: "1.2rem",
                  color: theme === "dark" ? "rgb(138, 182, 234)" : "rgb(54, 55, 75)",
                }}
              >
                {(!!initialUnread && initialUnread?.length > 0) ? (
                  <>
                    <div className='ps-2'>New Notifications</div>
                    <div
                      className='w-100 mt-3'
                      style={{
                        display: "grid",
                        maxWidth: "100%",
                        gridTemplateColumns: screenSize >= 1200 ? "1fr 1fr 1fr" : screenSize <= 1199 && screenSize >= 768 ? "1fr 1fr" : "1fr",
                        gap: "12px",
                      }}
                    >
                      {initialUnread.map((notification, idx) => {

                        let status = '';

                        if (notification.type === "transaction_update") {
                          if (notification.metadata?.txn_status === "COMPLETED") {
                            status = "TXN_SUCCESS"
                          } else if (notification.metadata?.txn_status === "REJECTED") {
                            status = "TXN_FAILED"
                          } else {
                            status = "DEFAULT"
                          }
                        } else if (notification.type === "request_status") {
                          if (notification.metadata?.request_status === "ACCEPTED") {
                            status = "REQ_ACCEPTED"
                          } else if (notification.metadata?.request_status === "PAYMENT-VERIFIED") {
                            status = "REQ_PAYMENT_VERIFIED"
                          } else if (notification.metadata?.request_status === "PAYMENT-REJECTED") {
                            status = "REQ_PAYMENT_REJECTED"
                          } else if (notification.metadata?.request_status === "REJECTED") {
                            status = "REQ_REJECTED"
                          } else {
                            status = "DEFAULT"
                          }
                        }

                        return (
                          <div className='col-12 col-sm p-0 animate-card' key={idx} style={{ minWidth: 0 }}>
                            <button
                              type='button'
                              className='row mx-auto align-items-center p-3 flex-nowrap column-gap-3 m-0 w-100'
                              // onClick={() => handleTransactionSelect(txn)}
                              style={{
                                border: "1px solid",
                                background: theme === 'dark' ? 'rgb(27, 33, 43)' : 'rgb(210 218 235)',
                                borderColor: theme === 'dark' ? 'rgb(63, 69, 93)' : 'rgb(143 170 192)',
                                boxShadow: theme === 'dark' ? '0px 0px 3px 1px rgba(12, 13, 17, .3)' : 'rgb(97 97 99 / 15%) 0px 0px 3px 1px',
                                borderRadius: "12px",
                              }}
                            >
                              <div
                                className='col-auto p-0 d-flex align-items-center justify-content-center flex-shrink-0'
                                style={{
                                  width: screenSize === "sm" ? "54px" : "62px",
                                  height: screenSize === "sm" ? "54px" : "62px",
                                  color: successTypes.includes(status) ? "rgb(84, 202, 52)" : rejectTypes.includes(status) ? "rgba(206, 26, 26, 1)" : "rgb(185, 185, 185)",
                                  border: "1px solid rgb(64, 103, 160)",
                                  background: "linear-gradient(to right bottom, rgb(62, 89, 136), rgb(27, 63, 107))",
                                  fontWeight: "600",
                                  fontSize: screenSize === "sm" ? "1.1rem" : "1.2rem",
                                  borderRadius: "12px",
                                  minWidth: 0,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {successTypes.includes(status) ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" width={34} height={34} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-progress-check"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M10 20.777a8.942 8.942 0 0 1 -2.48 -.969" /><path d="M14 3.223a9.003 9.003 0 0 1 0 17.554" /><path d="M4.579 17.093a8.961 8.961 0 0 1 -1.227 -2.592" /><path d="M3.124 10.5c.16 -.95 .468 -1.85 .9 -2.675l.169 -.305" /><path d="M6.907 4.579a8.954 8.954 0 0 1 3.093 -1.356" /><path d="M9 12l2 2l4 -4" /></svg>
                                ) : (rejectTypes.includes(status)) ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg>
                                ) : '#'}
                                {/* {account.value.bank_name?.split(' ').map((word) => {
                                          return word[0]
                                        })} */}
                              </div>
                              <div className='col p-0 d-flex flex-column justify-content-center align-items-start' style={{ minWidth: 0 }}>
                                <div
                                  className='text-start'
                                  style={{
                                    fontSize: "1.1rem",
                                    lineHeight: "1.1",
                                    color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                                    display: "-webkit-box",
                                    boxOrient: "vertical",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: "2",
                                    overflow: "hidden",
                                  }}
                                >
                                  {notification.title}
                                </div>
                                <div
                                  className='text-start'
                                  style={{
                                    fontSize: "13px",
                                    marginTop: "5px",
                                    lineHeight: "1.2",
                                    letterSpacing: '.03em',
                                    fontWeight: '400',
                                    color: theme === "dark" ? "rgba(255, 255, 255, 0.55)" : "rgba(40, 40, 40, 0.8)",
                                    // whiteSpace: "nowrap",
                                    // overflow: "hidden",
                                    // textOverflow: "ellipsis",
                                    width: "100%",
                                  }}
                                >
                                  {notification.message}
                                </div>
                                <div
                                  className='d-flex align-items-center flex-nowrap column-gap-2'
                                  style={{
                                    fontSize: "1rem",
                                    marginTop: "6px",
                                    color: theme === "dark" ? "rgb(150, 150, 150)" : "rgba(58, 57, 57, 1)",
                                  }}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width={16}
                                    height={16}
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth={theme === 'dark' ? 1.5 : 1.75}
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-clock-hour-4'
                                    style={{
                                      color: theme === "dark" ? "rgb(150, 150, 150)" : "rgba(22, 22, 162, 1)",
                                    }}
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                                    <path d='M12 12l3 2' />
                                    <path d='M12 7v5' />
                                  </svg>
                                  <span
                                    className=''
                                    style={{
                                      fontSize: ".9rem",
                                    }}
                                  >
                                    {formatTimeStr(notification.createdAt)}
                                  </span>
                                </div>
                              </div>
                              {/* <div className='col-auto p-0 d-flex flex-column justify-content-center align-items-end'>
                                <div
                                  className=''
                                  dir="ltr"
                                  style={{
                                    fontSize: "1rem",
                                    lineHeight: "1.1",
                                    color: getTransactionColor(txn.txnData, user, theme),
                                  }}
                                >
                                  <span
                                    className=""
                                    dir="ltr"
                                    style={{
                                      fontSize: ".85rem",
                                      marginRight: '.25rem',
                                      color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                                    }}
                                  >
                                    {t("app.fund.rupee")}
                                  </span>
                                  <span className='' dir="ltr">
                                    {numberWithCommas(amount.whole)}
                                    {amount.hasDecimal ? (
                                      <>
                                        .
                                        <span
                                          style={{
                                            fontSize: ".85rem",
                                          }}
                                        >
                                          {amount.decimal}
                                        </span>
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </span>
                                </div>
                                <div
                                  className=''
                                  style={{
                                    fontSize: "1rem",
                                    lineHeight: "1.1",
                                    color: getTransactionColor(txn.txnData, user, theme),
                                    marginTop: "5px",
                                  }}
                                >
                                  {numberWithCommas(units.whole)}
                                  {units.hasDecimal ? (
                                    <>
                                      .
                                      <span
                                        style={{
                                          fontSize: ".85rem",
                                        }}
                                      >
                                        {units.decimal}
                                      </span>
                                    </>
                                  ) : (
                                    ""
                                  )}
                                  <span
                                    className='ms-1'
                                    style={{
                                      fontSize: ".85rem",
                                      color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                                    }}
                                  >
                                    {t("app.fund.units")}
                                  </span>
                                </div>
                                <div
                                  className=''
                                  style={{
                                    fontSize: ".85rem",
                                    lineHeight: "1.1",
                                    color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                                    marginTop: "6px",
                                  }}
                                >
                                  {t("app.statement.balance")}
                                  <span
                                    className='ms-1'
                                    style={{
                                      fontSize: "1rem",
                                      color: theme === "dark" ? "rgb(86, 174, 247)" : "rgb(42, 35, 144)",
                                    }}
                                  >
                                    {balanceInMinus ? "-" + numberWithCommas(balance.whole) : numberWithCommas(balance.whole)}
                                    {balance.hasDecimal ? (
                                      <>
                                        .
                                        <span
                                          style={{
                                            fontSize: ".85rem",
                                          }}
                                        >
                                          {balance.decimal}
                                        </span>
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </span>
                                </div>
                              </div> */}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : ''}
              </div>

            {[...groups.entries()].map(([label, items], index) => (
              <div
                className='mb-3'
                key={index}
                style={{
                  fontSize: "1.2rem",
                  color: theme === "dark" ? "rgb(138, 182, 234)" : "rgb(54, 55, 75)",
                }}
              >
                <div className='ps-2'>{label}</div>
                <div
                  className='w-100 mt-3'
                  style={{
                    display: "grid",
                    maxWidth: "100%",
                    gridTemplateColumns: screenSize >= 1200 ? "1fr 1fr 1fr" : screenSize <= 1199 && screenSize >= 768 ? "1fr 1fr" : "1fr",
                    gap: "12px",
                  }}
                >
                  {items.map((notification, idx) => {

                    let status = '';

                    if (notification.type === "transaction_update") {
                      if (notification.metadata?.txn_status === "COMPLETED") {
                        status = "TXN_SUCCESS"
                      } else if (notification.metadata?.txn_status === "REJECTED") {
                        status = "TXN_FAILED"
                      } else {
                        status = "DEFAULT"
                      }
                    } else if (notification.type === "request_status") {
                      if (notification.metadata?.request_status === "ACCEPTED") {
                        status = "REQ_ACCEPTED"
                      } else if (notification.metadata?.request_status === "PAYMENT-VERIFIED") {
                        status = "REQ_PAYMENT_VERIFIED"
                      } else if (notification.metadata?.request_status === "PAYMENT-REJECTED") {
                        status = "REQ_PAYMENT_REJECTED"
                      } else if (notification.metadata?.request_status === "REJECTED") {
                        status = "REQ_REJECTED"
                      } else {
                        status = "DEFAULT"
                      }
                    }

                    return (
                      <div className='col-12 col-sm p-0 animate-card' key={idx} style={{ minWidth: 0 }}>
                        <button
                          type='button'
                          className='row mx-auto align-items-center p-3 flex-nowrap column-gap-3 m-0 w-100'
                          // onClick={() => handleTransactionSelect(txn)}
                          style={{
                            border: "1px solid",
                            background: theme === 'dark' ? 'rgb(27, 33, 43)' : 'rgb(210 218 235)',
                            borderColor: theme === 'dark' ? 'rgb(63, 69, 93)' : 'rgb(143 170 192)',
                            boxShadow: theme === 'dark' ? '0px 0px 3px 1px rgba(12, 13, 17, .3)' : 'rgb(97 97 99 / 15%) 0px 0px 3px 1px',
                            borderRadius: "12px",
                          }}
                        >
                          <div
                            className='col-auto p-0 d-flex align-items-center justify-content-center flex-shrink-0'
                            style={{
                              width: screenSize === "sm" ? "54px" : "62px",
                              height: screenSize === "sm" ? "54px" : "62px",
                              color: successTypes.includes(status) ? "rgb(84, 202, 52)" : rejectTypes.includes(status) ? "rgba(206, 26, 26, 1)" : "rgb(185, 185, 185)",
                              border: "1px solid rgb(64, 103, 160)",
                              background: "linear-gradient(to right bottom, rgb(62, 89, 136), rgb(27, 63, 107))",
                              fontWeight: "600",
                              fontSize: screenSize === "sm" ? "1.1rem" : "1.2rem",
                              borderRadius: "12px",
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                            }}
                          >
                            {successTypes.includes(status) ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width={34} height={34} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-progress-check"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M10 20.777a8.942 8.942 0 0 1 -2.48 -.969" /><path d="M14 3.223a9.003 9.003 0 0 1 0 17.554" /><path d="M4.579 17.093a8.961 8.961 0 0 1 -1.227 -2.592" /><path d="M3.124 10.5c.16 -.95 .468 -1.85 .9 -2.675l.169 -.305" /><path d="M6.907 4.579a8.954 8.954 0 0 1 3.093 -1.356" /><path d="M9 12l2 2l4 -4" /></svg>
                            ) : (rejectTypes.includes(status)) ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 9v4" /><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" /><path d="M12 16h.01" /></svg>
                            ) : '#'}
                            {/* {account.value.bank_name?.split(' ').map((word) => {
                                      return word[0]
                                    })} */}
                          </div>
                          <div className='col p-0 d-flex flex-column justify-content-center align-items-start' style={{ minWidth: 0 }}>
                            <div
                              className='text-start'
                              style={{
                                fontSize: "1.1rem",
                                lineHeight: "1.1",
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                                display: "-webkit-box",
                                boxOrient: "vertical",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: "2",
                                overflow: "hidden",
                              }}
                            >
                              {notification.title}
                            </div>
                            <div
                              className='text-start'
                              style={{
                                fontSize: "13px",
                                marginTop: "5px",
                                lineHeight: "1.2",
                                letterSpacing: '.03em',
                                fontWeight: '400',
                                color: theme === "dark" ? "rgba(255, 255, 255, 0.55)" : "rgba(40, 40, 40, 0.8)",
                                // whiteSpace: "nowrap",
                                // overflow: "hidden",
                                // textOverflow: "ellipsis",
                                width: "100%",
                              }}
                            >
                              {notification.message}
                            </div>
                            <div
                              className='d-flex align-items-center flex-nowrap column-gap-2'
                              style={{
                                fontSize: "1rem",
                                marginTop: "6px",
                                color: theme === "dark" ? "rgb(150, 150, 150)" : "rgba(58, 57, 57, 1)",
                              }}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width={16}
                                height={16}
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={theme === 'dark' ? 1.5 : 1.75}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-clock-hour-4'
                                style={{
                                  color: theme === "dark" ? "rgb(150, 150, 150)" : "rgba(22, 22, 162, 1)",
                                }}
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                                <path d='M12 12l3 2' />
                                <path d='M12 7v5' />
                              </svg>
                              <span
                                className=''
                                style={{
                                  fontSize: ".9rem",
                                }}
                              >
                                {formatTimeStr(notification.createdAt)}
                              </span>
                            </div>
                          </div>
                          {/* <div className='col-auto p-0 d-flex flex-column justify-content-center align-items-end'>
                            <div
                              className=''
                              dir="ltr"
                              style={{
                                fontSize: "1rem",
                                lineHeight: "1.1",
                                color: getTransactionColor(txn.txnData, user, theme),
                              }}
                            >
                              <span
                                className=""
                                dir="ltr"
                                style={{
                                  fontSize: ".85rem",
                                  marginRight: '.25rem',
                                  color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                                }}
                              >
                                {t("app.fund.rupee")}
                              </span>
                              <span className='' dir="ltr">
                                {numberWithCommas(amount.whole)}
                                {amount.hasDecimal ? (
                                  <>
                                    .
                                    <span
                                      style={{
                                        fontSize: ".85rem",
                                      }}
                                    >
                                      {amount.decimal}
                                    </span>
                                  </>
                                ) : (
                                  ""
                                )}
                              </span>
                            </div>
                            <div
                              className=''
                              style={{
                                fontSize: "1rem",
                                lineHeight: "1.1",
                                color: getTransactionColor(txn.txnData, user, theme),
                                marginTop: "5px",
                              }}
                            >
                              {numberWithCommas(units.whole)}
                              {units.hasDecimal ? (
                                <>
                                  .
                                  <span
                                    style={{
                                      fontSize: ".85rem",
                                    }}
                                  >
                                    {units.decimal}
                                  </span>
                                </>
                              ) : (
                                ""
                              )}
                              <span
                                className='ms-1'
                                style={{
                                  fontSize: ".85rem",
                                  color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                                }}
                              >
                                {t("app.fund.units")}
                              </span>
                            </div>
                            <div
                              className=''
                              style={{
                                fontSize: ".85rem",
                                lineHeight: "1.1",
                                color: theme === "dark" ? "rgb(159, 159, 159)" : "rgba(60, 60, 60, 1)",
                                marginTop: "6px",
                              }}
                            >
                              {t("app.statement.balance")}
                              <span
                                className='ms-1'
                                style={{
                                  fontSize: "1rem",
                                  color: theme === "dark" ? "rgb(86, 174, 247)" : "rgb(42, 35, 144)",
                                }}
                              >
                                {balanceInMinus ? "-" + numberWithCommas(balance.whole) : numberWithCommas(balance.whole)}
                                {balance.hasDecimal ? (
                                  <>
                                    .
                                    <span
                                      style={{
                                        fontSize: ".85rem",
                                      }}
                                    >
                                      {balance.decimal}
                                    </span>
                                  </>
                                ) : (
                                  ""
                                )}
                              </span>
                            </div>
                          </div> */}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* -- TXN DETAIL VIEW - MODAL -- */}
            {/* <div className='modal fade' id='txnModal' tabIndex='-1' aria-labelledby='txnModal' aria-hidden='true' ref={transactionModalRef}>
              <div
                className='modal-dialog modal-dialog-centered'
                style={{
                  ["--bs-modal-width"]: "800px",
                  marginInline: screenSize >= 830 ? "auto" : ".5rem",
                }}
              >
                <div
                  className='modal-content'
                  style={{
                    // backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
                    borderRadius: "20px",
                    border: "none",
                  }}
                >
                  <div className='modal-body p-0 position-relative'>
                    <TransactionDetail data={activeTransaction} txnType={getTransactionType} />
                  </div>
                </div>
              </div>
            </div> */}
          </div>
          {/* )} */}
        </div>
    </div>
	</div>

  );
}
